import React, { useState, useEffect, useCallback } from "react";
import { useMsal }          from "@azure/msal-react";
import AuthContext          from "../AuthContext";
import { getFullUserProfile }               from "../../../services/auth";
import { ALLOWED_ROLES, ROLE_DEFAULT_ROUTES } from "../../../constants/auth";
import { getCurrentUser }   from "../../../api/users";

const LOGOUT_BROADCAST_KEY = "psi360_auth_logout";

const AuthProvider = ({ children }) => {
  const { instance, accounts, inProgress } = useMsal();
  const isMsalBusy = inProgress !== "none";
  const [user,      setUser]      = useState(null);
  const [userInfo,  setUserInfo]  = useState(null);
  const [userRole,  setUserRole]  = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  

  const login = useCallback(async () => {
    setAuthError(null);
    await instance.loginRedirect();
  }, [instance]);

  const logout = useCallback(() => {
    localStorage.setItem(LOGOUT_BROADCAST_KEY, Date.now().toString());
    instance.logoutRedirect();
  }, [instance]);

  const silentLogout = useCallback(async () => {
    try {
      const account = instance.getActiveAccount();
      if (account) await instance.clearCache();
    } catch (err) {
      console.warn("Silent logout error:", err);
    }
  }, [instance]);

  const extractRole = useCallback((account) => {
    const roles   = account?.idTokenClaims?.roles || [];
    if (roles.length === 0) return null;
    const matched = roles.find((role) =>
      ALLOWED_ROLES.some(
        (allowedRole) =>
          allowedRole.trim().toLowerCase() === role.trim().toLowerCase()
      )
    );
    return matched ? matched.trim() : null;
  }, []);

  useEffect(() => {
    const initializeUser = async () => {
      if (accounts.length === 0) { setLoading(false); return; }

      const activeAccount = instance.getActiveAccount() || accounts[0];
      instance.setActiveAccount(activeAccount);

      // ── DEBUG LOGS ──────────────────────────────────────────────
      console.log("AD token claims:", activeAccount?.idTokenClaims);
      console.log("OID:", activeAccount?.idTokenClaims?.oid);
      // ────────────────────────────────────────────────────────────

      try {
        setLoading(true);

        const role = extractRole(activeAccount);

        if (!role) {
          setAuthError("Your account does not have access to PSI360. Please contact your administrator to get the correct role assigned.");
          await silentLogout();
          setLoading(false);
          return;
        }

        setUserRole(role);

        let baseUser = {
          email:        activeAccount.username,
          fullName:     activeAccount.name,
          photoUrl:     null,
          role,
          defaultRoute: ROLE_DEFAULT_ROUTES[role] || "/dashboard",
          macId:        null,
          macCode:      null,
          macName:      null,
        };

        try {
          const graphProfile = await getFullUserProfile();
          setUserInfo(graphProfile);
          baseUser = {
            ...baseUser,
            email:    graphProfile.mail || graphProfile.userPrincipalName,
            fullName: graphProfile.displayName,
            photoUrl: graphProfile.photoUrl,
          };
        } catch (graphErr) {
          console.warn("Graph profile fetch failed — using token claims:", graphErr);
        }

        try {
          const psiUser = await getCurrentUser();
          baseUser = {
            ...baseUser,
            macId:   psiUser.macId   ?? null,
            macCode: psiUser.macCode ?? null,
            macName: psiUser.macName ?? null,
            role:    psiUser.role    ?? role,
          };
        } catch (psiErr) {
          console.warn("PSI360 /users/me failed — MAC fields unavailable:", psiErr);
        }

        setUser(baseUser);

        // ── DEBUG LOG ────────────────────────────────────────────
        console.log("App role:", baseUser.role);
        // ────────────────────────────────────────────────────────

      } catch (err) {
        setAuthError(err?.message || "Login failed. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    initializeUser();

    const handleStorageBroadcast = (e) => {
      if (e.key === LOGOUT_BROADCAST_KEY) instance.logoutRedirect();
    };

    window.addEventListener("storage",  handleStorageBroadcast);
    return () => window.removeEventListener("storage", handleStorageBroadcast);
  }, [accounts, instance, extractRole, silentLogout]);

  const contextValue = {
    user,
    userInfo,
    userRole,
    loading: loading || isMsalBusy,
    authError,
    login,
    logout,
    clearAuthError:  () => setAuthError(null),
    isAuthenticated: !!user,
    hasRole:         (role)  => userRole === role,
    hasAnyRole:      (roles) => roles.includes(userRole),
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;